import React, { useEffect, useState } from 'react';
import { UserPlus, Check } from 'lucide-react';

// ── Badge helpers ──────────────────────────────────────────────────────────────
export function getBadge(rank) {
  const r = parseInt(rank);
  if (r <= 100)  return { emoji: '🥇', label: 'Gold',   color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)' };
  if (r <= 500)  return { emoji: '🥈', label: 'Silver', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.3)' };
  if (r <= 1000) return { emoji: '🥉', label: 'Bronze', color: '#cd7c2f', bg: 'rgba(205,124,47,0.1)',   border: 'rgba(205,124,47,0.3)' };
  return null;
}

export function BadgePill({ rank }) {
  const badge = getBadge(rank);
  if (!badge) return null;
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: 20,
      background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
      display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
    }}>
      {badge.emoji} {badge.label}
    </span>
  );
}

export function RankDisplay({ rank }) {
  const r = parseInt(rank);
  if (r === 1) return <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>🥇</span>;
  if (r === 2) return <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>🥈</span>;
  if (r === 3) return <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>🥉</span>;
  return (
    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', fontFamily: 'var(--font-display)' }}>
      #{r}
    </span>
  );
}

const AVATAR_COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#ec4899'];
export function LBAvatar({ name, avatarUrl, size = 34 }) {
  const [showImage, setShowImage] = useState(Boolean(avatarUrl));
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

  useEffect(() => {
    setShowImage(Boolean(avatarUrl));
  }, [avatarUrl]);

  if (avatarUrl && showImage) {
    return (
      <img
        src={avatarUrl}
        alt={name || 'avatar'}
        style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', border: `2px solid ${color}44` }}
        onError={() => setShowImage(false)}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color + '20', color, border: `2px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size > 36 ? '0.88rem' : '0.7rem',
    }}>
      {initials}
    </div>
  );
}

// Normalise both global and contest entry shapes into one consistent object
function norm(entry) {
  return {
    id:             entry.id || entry.user_id,
    rank:           entry.rank,
    name:           entry.display_name || entry.username || '?',
    avatar_url:     entry.avatar_url || null,
    holdings_count: entry.holdings_count || 0,
    cash:           parseFloat(entry.cash_points  || 0),
    hold:           parseFloat(entry.portfolio_value || 0),
    total:          parseFloat(entry.total_value   || 0),
  };
}

// Column template constants — import in page headers so they always match
export const COLS_WITH_ADD = '40px minmax(0,1fr) 62px 68px 86px 72px';
export const COLS_NO_ADD   = '40px minmax(0,1fr) 62px 68px 86px';

// ── Desktop table header ───────────────────────────────────────────────────────
export function LBHeader({ showAdd = false }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: showAdd ? COLS_WITH_ADD : COLS_NO_ADD,
      gap: 0,
      padding: '9px 1.25rem',
      borderBottom: '1px solid var(--border)',
      borderLeft: '3px solid transparent',
      fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.6px',
      background: 'var(--bg-secondary)',
    }}>
      <span style={{ textAlign: 'center' }}>#</span>
      <span style={{ paddingLeft: 39 }}>Trader</span>
      <span style={{ textAlign: 'right', paddingRight: 8 }}>Cash</span>
      <span style={{ textAlign: 'right', paddingRight: 8 }}>Hold</span>
      <span style={{ textAlign: 'right', paddingRight: showAdd ? 8 : 0 }}>Total</span>
      {showAdd && <span />}
    </div>
  );
}

// ── Desktop row ────────────────────────────────────────────────────────────────
// showAddCol: controls the grid template (must match the header). Pass true for
// global leaderboard, false for contest. The button itself only renders when
// the user is not "me" and is not already a friend.
export function LBRow({ entry, isMe, requestState, onSendRequest, showAddCol = true }) {
  const d = norm(entry);
  const badge      = getBadge(d.rank);
  const showButton = showAddCol && !isMe && requestState !== 'friends';

  // Top-3 left-border colour
  const rankColor = d.rank === 1 ? '#fbbf24' : d.rank === 2 ? '#94a3b8' : d.rank === 3 ? '#cd7c2f' : null;
  const leftBorder = isMe
    ? '3px solid var(--accent)'
    : rankColor
      ? `3px solid ${rankColor}55`
      : '3px solid transparent';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: showAddCol ? COLS_WITH_ADD : COLS_NO_ADD,
        gap: 0,
        alignItems: 'center',
        padding: '0 1.25rem',
        height: 58,
        borderBottom: '1px solid var(--border)',
        background: isMe ? 'rgba(245,158,11,0.06)' : 'transparent',
        borderLeft: leftBorder,
        transition: 'background 0.12s',
        cursor: 'default',
      }}
      onMouseEnter={e => { if (!isMe) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
      onMouseLeave={e => { if (!isMe) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Rank */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <RankDisplay rank={d.rank} />
      </div>

      {/* Avatar + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, paddingRight: 8 }}>
        <LBAvatar name={d.name} avatarUrl={d.avatar_url} size={30} />
        <div style={{ minWidth: 0 }}>
          <span style={{
            fontWeight: 600, fontSize: '0.85rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'block', maxWidth: '100%',
          }}>{d.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
            {isMe && (
              <span style={{
                fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: 20,
                background: 'rgba(245,158,11,0.15)', color: 'var(--accent)',
                border: '1px solid rgba(245,158,11,0.3)', flexShrink: 0,
              }}>you</span>
            )}
            {badge
              ? <BadgePill rank={d.rank} />
              : <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>{d.holdings_count} stock{d.holdings_count !== 1 ? 's' : ''}</span>
            }
          </div>
        </div>
      </div>

      {/* Cash */}
      <div style={{ textAlign: 'right', paddingRight: 8 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
          {d.cash.toFixed(0)}
        </div>
        <div style={{ fontSize: '0.59rem', color: 'var(--text-muted)', marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.3px' }}>cash</div>
      </div>

      {/* Hold */}
      <div style={{ textAlign: 'right', paddingRight: 8 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#60a5fa' }}>
          {d.hold.toFixed(0)}
        </div>
        <div style={{ fontSize: '0.59rem', color: 'var(--text-muted)', marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.3px' }}>hold</div>
      </div>

      {/* Total */}
      <div style={{ textAlign: 'right', paddingRight: showAddCol ? 8 : 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1rem',
          color: 'var(--accent)', lineHeight: 1,
        }}>
          {d.total.toFixed(0)}
        </div>
        <div style={{ fontSize: '0.59rem', color: 'var(--text-muted)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>pts</div>
      </div>

      {/* Add friend column — always present when showAddCol, button only when eligible */}
      {showAddCol && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {showButton && (
            <button
              className={requestState === 'sent' ? 'btn btn-outline' : 'btn btn-buy'}
              style={{ padding: '4px 8px', fontSize: '0.67rem', display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}
              onClick={() => onSendRequest?.(d.id)}
              disabled={requestState === 'sent'}
            >
              {requestState === 'sent'
                ? <><Check size={10} /> Sent</>
                : <><UserPlus size={10} /> Add</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Mobile card ────────────────────────────────────────────────────────────────
export function LBCard({ entry, isMe, requestState, onSendRequest }) {
  const d = norm(entry);
  const badge   = getBadge(d.rank);
  const showAdd = !isMe && requestState !== 'friends';

  const rankColor = d.rank === 1 ? '#fbbf24' : d.rank === 2 ? '#94a3b8' : d.rank === 3 ? '#cd7c2f' : null;
  const leftBorder = isMe
    ? '3px solid var(--accent)'
    : rankColor
      ? `3px solid ${rankColor}55`
      : '3px solid transparent';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '11px 14px',
      borderBottom: '1px solid var(--border)',
      background: isMe ? 'rgba(245,158,11,0.06)' : 'transparent',
      borderLeft: leftBorder,
    }}>

      {/* Rank */}
      <div style={{ width: 26, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <RankDisplay rank={d.rank} />
      </div>

      {/* Avatar */}
      <LBAvatar name={d.name} avatarUrl={d.avatar_url} size={34} />

      {/* Name + badge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontWeight: 600, fontSize: '0.84rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          display: 'block', maxWidth: '100%',
        }}>{d.name}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
          {isMe && (
            <span style={{
              fontSize: '0.57rem', fontWeight: 700, padding: '1px 5px', borderRadius: 20,
              background: 'rgba(245,158,11,0.15)', color: 'var(--accent)',
              border: '1px solid rgba(245,158,11,0.3)', flexShrink: 0,
            }}>you</span>
          )}
          {badge
            ? <BadgePill rank={d.rank} />
            : <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{d.holdings_count} stocks</span>
          }
        </div>
      </div>

      {/* Right: stats + optional add */}
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        {/* Total */}
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1rem',
          color: 'var(--accent)', lineHeight: 1,
        }}>
          {d.total.toFixed(0)}
          <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)', marginLeft: 2, fontFamily: 'inherit', fontWeight: 400 }}>pts</span>
        </div>

        {/* Cash + Hold sub-row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'flex-end', marginTop: 4 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: '0.67rem', background: 'var(--bg-secondary)',
            border: '1px solid var(--border)', borderRadius: 6,
            padding: '1px 5px', color: 'var(--text-secondary)',
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.6rem' }}>cash</span>
            {d.cash.toFixed(0)}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: '0.67rem', background: 'rgba(96,165,250,0.08)',
            border: '1px solid rgba(96,165,250,0.2)', borderRadius: 6,
            padding: '1px 5px', color: '#60a5fa',
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.6rem' }}>hold</span>
            {d.hold.toFixed(0)}
          </span>
        </div>

        {/* Add button */}
        {showAdd && (
          <button
            className={requestState === 'sent' ? 'btn btn-outline' : 'btn btn-buy'}
            style={{ padding: '3px 8px', fontSize: '0.66rem', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 6, whiteSpace: 'nowrap' }}
            onClick={() => onSendRequest?.(d.id)}
            disabled={requestState === 'sent'}
          >
            {requestState === 'sent'
              ? <><Check size={10} /> Sent</>
              : <><UserPlus size={10} /> Add</>}
          </button>
        )}
      </div>
    </div>
  );
}
